import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompany, createCompany, updateCompany } from "@/services/firebase/company";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";
import { AdminPage, AdminPageHeader, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const companySchema = z.object({
  company_id: z.string().min(1, "公司 ID 為必填"),
  company_name: z.string().min(1, "公司名稱為必填"),
  company_logo: z.string().optional(),
  website: z
    .string()
    .optional()
    .refine(
      (val) => !val || val === "" || z.string().url().safeParse(val).success,
      "請輸入有效的網址"
    ),
  description: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

export default function CompanyEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入製作公司數據
  const { data: company, isLoading } = useQuery({
    queryKey: ["company", actualId],
    queryFn: () => getCompany(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      company_id: "",
      company_name: "",
      company_logo: "",
      website: "",
      description: "",
    },
  });

  const companyLogo = watch("company_logo");

  // 初始化表單數據
  useEffect(() => {
    if (company && !isNew) {
      setValue("company_id", company.company_id || "");
      setValue("company_name", company.company_name || "");
      setValue("company_logo", company.company_logo || "");
      setValue("website", company.website || "");
      setValue("description", company.description || "");
    }
  }, [company, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      if (isNew) {
        return await createCompany({
          company_id: data.company_id,
          company_name: data.company_name,
          company_logo: data.company_logo,
          website: data.website || undefined,
          description: data.description,
        });
      } else {
        if (!actualId) {
          throw new Error("缺少製作公司 ID");
        }
        await updateCompany(actualId, {
          company_id: data.company_id,
          company_name: data.company_name,
          company_logo: data.company_logo,
          website: data.website || undefined,
          description: data.description,
        });
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["company", savedId] });
      setSaveError(null);
      navigate("/companies");
    },
    onError: (error) => {
      console.error("儲存製作公司失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: CompanyFormData) => {
    mutation.mutate(data);
  };

  const handleImageUpload = (url: string) => {
    setValue("company_logo", url, { shouldDirty: true, shouldValidate: true });
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增製作公司" : "編輯製作公司"}
        actions={
          <Button variant="outline" onClick={() => navigate("/companies")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 錯誤訊息 */}
        {saveError && (
          <AdminErrorAlert
            message={`儲存失敗：${saveError}`}
            onDismiss={() => setSaveError(null)}
          />
        )}

        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_id">
                公司 ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="company_id"
                {...register("company_id")}
                placeholder="例如：company_001"
                disabled={!isNew}
                className={errors.company_id ? "border-destructive" : ""}
              />
              {errors.company_id && (
                <p className="text-sm text-destructive">{errors.company_id.message}</p>
              )}
              {!isNew && (
                <p className="text-xs text-muted-foreground">
                  公司 ID 建立後無法修改
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">
                公司名稱 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="company_name"
                {...register("company_name")}
                placeholder="例如：ABC 製作公司"
                className={errors.company_name ? "border-destructive" : ""}
              />
              {errors.company_name && (
                <p className="text-sm text-destructive">{errors.company_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">公司網站</Label>
              <Input
                id="website"
                type="url"
                {...register("website")}
                placeholder="https://example.com"
                className={errors.website ? "border-destructive" : ""}
              />
              {errors.website && (
                <p className="text-sm text-destructive">{errors.website.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle>Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <ImageUpload
              value={companyLogo}
              onChange={handleImageUpload}
              storagePath="company_logos"
              label="公司 Logo"
              required={false}
            />
          </CardContent>
        </Card>

        {/* 簡介 */}
        <Card>
          <CardHeader>
            <CardTitle>公司簡介</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="description">簡介</Label>
              <Textarea
                id="description"
                {...register("description")}
                rows={6}
                placeholder="輸入製作公司的詳細介紹..."
              />
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/companies")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
